"use client"

import * as Sentry from "@sentry/nextjs"
import Link from "next/link"
import { startTransition, useEffect } from "react"
import { useLocalePath } from "@/lib/use-localized-navigation"

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const lp = useLocalePath()

  // Surface the underlying error (the one that triggered this boundary)
  // to Sentry with a proper exception mechanism, instead of relying on the
  // auto console-capture which loses the throwing component context. The
  // locale-scoped boundary previously logged nothing — only `global-error`
  // did — so a render-time exception inside `[country]/[locale]/*` would
  // surface in Sentry as a generic React #185 ("Maximum update depth")
  // from the reconciler internals with no app stack.
  useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  // Wrap `reset()` in startTransition: if the underlying error still
  // throws synchronously on re-render, React's reconciler can keep
  // re-entering the same render → error → reset cycle and trip the
  // "Maximum update depth exceeded" safety net inside the boundary
  // itself. startTransition defers the reset update so React can bail
  // out instead of looping (see Next.js error.tsx docs).
  function handleReset() {
    startTransition(() => reset())
  }

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-4">
      <h2 className="text-aubergine-900 text-2xl font-normal">
        Something went wrong
      </h2>
      <p className="max-w-md text-center text-neutral-500">
        An unexpected error occurred. Please try again or return to the
        homepage.
      </p>
      <div className="flex gap-3">
        <button
          onClick={handleReset}
          className="bg-aubergine-800 hover:bg-aubergine-900 cursor-pointer rounded-full px-6 py-2.5 text-sm font-medium text-white transition-colors"
        >
          Try again
        </button>
        <Link
          href={lp("/")}
          className="rounded-full border border-neutral-300 px-6 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
        >
          Go home
        </Link>
      </div>
    </div>
  )
}
